import React, { useState } from "react";
import { useParams } from "react-router-dom";

import moment from "moment-timezone";
import sortBy from "lodash/sortBy";

import Form from "components/Form";
import FormSubmit from "components/FormSubmit";
import PageContent from "components/PageContent";
import Panel from "components/Panel";
import PanelSection from "components/PanelSection";
import SettingsNav from "components/SettingsNav";
import CopyToClipboardIcon from "components/CopyToClipboardIcon";

import styles from "./style.module.scss";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye } from "@fortawesome/pro-light-svg-icons";
import { faTrashAlt } from "@fortawesome/pro-light-svg-icons";

import { ApiKeysQuery, useApiKeysQuery } from "./gql/Query.generated";
import { useRoutes } from "utils/routes";
import { formatDateShort } from "utils/format";
import Loading from "components/Loading";
import Grid from "components/Grid";
import Button from "components/Button";
import { useFeature } from "components/OrganizationFeatures";
import { useSetFlash } from "components/WithFlashes";
import { OAuthApplicationsPanel, OAuthAdminPanel } from "./OAuthSettings";
import {
  OAuthAdminDocument,
  useOAuthAdminQuery,
} from "./gql/AdminQuery.generated";
import { useUpdateOrganizationApiSettingsMutation } from "./gql/UpdateApiSettings.generated";
import UpgradeRequired from "components/UpgradeRequired";

type ApiKeyType = Omit<ApiKeysQuery["getApiKeys"][number], "__typename">;

const ApiAccess = () => {
  const { slug: organizationSlug } = useParams();
  return (
    <PageContent
      title="Settings"
      pageCategory="api-keys"
      pageName="index"
      featureNav={<SettingsNav />}
    >
      <ApiKeysContent organizationSlug={organizationSlug} />
      <ApiSettingsPanels organizationSlug={organizationSlug} />
    </PageContent>
  );
};

export const ApiKeysContent: React.FunctionComponent<{
  organizationSlug: string;
}> = ({ organizationSlug }) => {
  const { organizationApi } = useRoutes();
  const { data, loading, error } = useApiKeysQuery({
    variables: { organizationSlug },
  });
  if (loading || error) {
    return <Loading error={!!error} />;
  }

  const apiKeys = sortBy(
    data.getApiKeys,
    (k: ApiKeyType): number | null | undefined => k.lastUsedAt,
  );
  const { slug, permittedToCreateApiKeys, permittedToCreateRWApiKeys } =
    data.getOrganizationDetails;

  return (
    <Panel title="API Keys">
      <Grid
        data={apiKeys}
        defaultSortBy="createdAt"
        columns={[
          {
            field: "token",
            header: "Key",
            width: "320px",
            renderer: function TokenCell({ fieldData }) {
              return <Token token={fieldData} />;
            },
          },
          {
            field: "accessScopeType",
            header: "Scope",
            width: "1fr",
            renderer: function ScopeCell({ rowData }) {
              return (
                <>
                  {rowData.accessScopeType}: {rowData.accessScopeName}
                </>
              );
            },
          },
          { field: "accessType", header: "Access Type", width: "120px" },
          {
            field: "lastUsedAt",
            header: "Last Used At",
            width: "120px",
            nullValue: "-",
            renderer: function LastUsedAtCell({ fieldData }) {
              return formatDateShort(moment.unix(fieldData));
            },
          },
          {
            field: "createdAt",
            header: "Created At",
            width: "120px",
            renderer: function CreatedAtCell({ fieldData }) {
              return formatDateShort(moment.unix(fieldData));
            },
          },
          {
            field: "id",
            header: "",
            width: "40px",
            renderer: function RevokeCell({ rowData }) {
              return (
                <a
                  href={`/organizations/${organizationSlug}/api/${rowData.id}`}
                  data-method="delete"
                  data-confirm={`Revoke the API key of ${rowData.accessScopeType.toLowerCase()} ${
                    rowData.accessScopeName
                  } (${rowData.accessType})?`}
                >
                  <FontAwesomeIcon
                    icon={faTrashAlt}
                    title="Revoke"
                    className="text-[#CA1515] cursor-pointer"
                  />
                </a>
              );
            },
          },
        ]}
      />
      {permittedToCreateApiKeys ? (
        <>
          <PanelSection>
            <p>
              Choose the access type of the new API key (the key will be created
              at the organization level):
            </p>
            <Form action={organizationApi(slug)} insideSection>
              <div className="form-group">
                <input
                  type="radio"
                  name="scope"
                  value="read"
                  id="read_scope"
                  defaultChecked
                />{" "}
                <label className="text-[#555]" htmlFor="read_scope">
                  Read API Key
                </label>
                <div className="row">
                  <div className="col-sm-12">
                    <div className="help-block">
                      Can be used to retrieve statistics data and pganalyze
                      settings with the pganalyze GraphQL API
                    </div>
                  </div>
                </div>
                <input
                  type="radio"
                  name="scope"
                  value="collector"
                  id="collector_scope"
                />{" "}
                <label className="text-[#555]" htmlFor="collector_scope">
                  Collector API Key
                </label>
                <div className="row">
                  <div className="col-sm-12">
                    <div className="help-block">
                      Can be used by the collector to send database statistics
                      snapshots to pganalyze
                    </div>
                  </div>
                </div>
                {permittedToCreateRWApiKeys && (
                  <>
                    <input
                      type="radio"
                      name="scope"
                      value="read_write"
                      id="read_write_scope"
                    />{" "}
                    <label className="text-[#555]" htmlFor="read_write_scope">
                      Read-Write API Key
                    </label>
                    <div className="row">
                      <div className="col-sm-12">
                        <div className="help-block">
                          Can be used to retrieve statistics data and pganalyze
                          settings, and change pganalyze settings with the
                          pganalyze GraphQL API
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
              <FormSubmit title="Create new API Key" />
            </Form>
          </PanelSection>
        </>
      ) : (
        <PanelSection>
          <strong>Permission Error:</strong> You are not allowed to view or
          create API keys in this organization.
        </PanelSection>
      )}
    </Panel>
  );
};

export const ApiSettingsPanels = ({
  organizationSlug,
}: {
  organizationSlug: string;
}) => {
  const { data, loading, error } = useOAuthAdminQuery({
    variables: { organizationSlug },
  });

  if (loading || error) {
    return <Loading error={!!error} />;
  }

  const { mcpEnabled, permittedToManageApiSettings } =
    data.getOrganizationDetails;

  return (
    <>
      <McpSettingsPanel
        organizationSlug={organizationSlug}
        mcpEnabled={mcpEnabled}
        permittedToManageApiSettings={permittedToManageApiSettings}
      />
      {permittedToManageApiSettings && (
        <OAuthAdminPanel organizationSlug={organizationSlug} />
      )}
      <OAuthApplicationsPanel organizationSlug={organizationSlug} />
    </>
  );
};

export const McpSettingsPanel = ({
  organizationSlug,
  mcpEnabled,
  permittedToManageApiSettings,
}: {
  organizationSlug: string;
  mcpEnabled: boolean;
  permittedToManageApiSettings: boolean;
}) => {
  const setFlash = useSetFlash();
  const hasMcpServer = useFeature("mcpServer");

  const [updateSettings, { loading: updateLoading }] =
    useUpdateOrganizationApiSettingsMutation();

  const handleToggleMcp = () => {
    const newEnabled = !mcpEnabled;
    const action = newEnabled ? "enable" : "disable";
    if (
      !window.confirm(
        `Are you sure you want to ${action} the MCP server for this organization?`,
      )
    ) {
      return;
    }
    updateSettings({
      variables: {
        organizationSlug,
        mcpEnabled: newEnabled,
      },
      onCompleted: () => {
        setFlash(
          "success",
          `MCP server has been ${newEnabled ? "enabled" : "disabled"}.`,
        );
      },
      onError: (error) => {
        setFlash("error", error.message);
      },
      refetchQueries: [
        { query: OAuthAdminDocument, variables: { organizationSlug } },
      ],
    });
  };

  return hasMcpServer ? (
    <Panel
      title="MCP Server Access (Preview)"
      secondaryTitle={
        <a href="https://pganalyze.com/docs/mcp" target="_blank">
          Learn more
        </a>
      }
    >
      <PanelSection>
        <p>
          The MCP server is{" "}
          <strong>{mcpEnabled ? "enabled" : "disabled"}</strong>. MCP clients{" "}
          {mcpEnabled ? "can" : "cannot"} access data of this organization.
        </p>
        {permittedToManageApiSettings && (
          <button
            onClick={handleToggleMcp}
            disabled={updateLoading}
            className="btn btn-success"
          >
            {mcpEnabled ? "Disable" : "Enable"} MCP Server
          </button>
        )}
      </PanelSection>
    </Panel>
  ) : (
    <UpgradeRequired
      feature="MCP Server"
      title="MCP Server Access (Preview)"
      panelOnly
    />
  );
};

const Token: React.FunctionComponent<{ token: string }> = ({ token }) => {
  const [show, setShow] = useState(false);

  const handleShow = () => {
    setShow(true);
  };

  let tokenText;
  let icon;
  if (show) {
    tokenText = token;
    icon = <CopyToClipboardIcon content={token} />;
  } else {
    // mask with * except beginning of token
    const unmaskedLen = /^pga[cwr]_/.test(token)
      ? 8 // for new-style tokens, we have a fixed prefix, so we can show a bit past that
      : 3; // otherwise just show the first three characters

    if (token.length >= unmaskedLen) {
      tokenText =
        token.substring(0, unmaskedLen) +
        "*".repeat(token.length - unmaskedLen);
    } else {
      tokenText = "*".repeat(token.length);
    }
    icon = (
      <Button bare onClick={handleShow} className={styles.icon}>
        <FontAwesomeIcon icon={faEye} title="Show" fixedWidth />
      </Button>
    );
  }

  return (
    <div className="flex justify-between">
      <code className={styles.token}>{tokenText}</code> {icon}
    </div>
  );
};

export default ApiAccess;
